import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useAuthenticatedUser } from '../../../common/useAuthenticatedUser'
import { type BookingClient } from '../../../models/Scheduling'
import { useGetUnsubscribesQuery } from '../../../store/scheduling/scheduling'
import { hasClientEmail } from './clientEmailGaps'

export const MAX_UNSUBSCRIBE_LOOKUP_EMAILS = 50

export const normalizeEmail = (email: string): string => email.trim().toLowerCase()

const normalizedClientEmails = (clients: BookingClient[]): string[] =>
  clients.flatMap((client) => (client.email != null && hasClientEmail(client) ? [normalizeEmail(client.email)] : []))

export const useUnsubscribedClients = (clients: BookingClient[]): BookingClient[] => {
  const accountId = useAuthenticatedUser().accountId
  const emails = useMemo(
    () =>
      Array.from(new Set(normalizedClientEmails(clients)))
        .sort()
        .slice(0, MAX_UNSUBSCRIBE_LOOKUP_EMAILS),
    [clients],
  )
  const { data } = useGetUnsubscribesQuery(emails.length > 0 ? { accountId, emails } : skipToken)

  return useMemo(() => {
    if (data == null) {
      return []
    }
    const blocked = new Set(data.map((entry) => normalizeEmail(entry.email)))
    return clients.filter((client) => client.email != null && blocked.has(normalizeEmail(client.email)))
  }, [clients, data])
}
